/*!
 * AudoChat - Items Management Page
 * Connected to PostgreSQL Database
 */

import { useState, useEffect } from "react";

// @mui material components
import { TextField, IconButton, Card, Grid, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// React icons
import { MdAdd, MdEdit, MdDelete } from "react-icons/md";
import { IoCubeOutline } from "react-icons/io5";
import { BsTable, BsGrid3X3 } from "react-icons/bs";

// API
import api from "utils/api";

const { secondary, info, text, dark, gradients, borderCol } = colors;
const { card } = gradients;

// Helper function to detect if text contains Arabic characters
const containsArabic = (text) => {
  if (!text) return false;
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

function Items() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, outOfStock: 0, totalValue: 0 });
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // "table" or "grid"
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    stock: "",
  });

  // Load items from database
  useEffect(() => {
    loadItems();
    loadStats();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const items = await api.getItems();
      setItems(items || []);
    } catch (error) {
      console.error("Error loading items:", error);
      alert("Failed to load items: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const stats = await api.getItemStats();
      setStats(stats || { total: 0, active: 0, outOfStock: 0, totalValue: 0 });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || "",
        description: item.description || "",
        price: item.price?.toString() || "",
        category: item.category || "",
        stock: item.stock_quantity?.toString() || "",
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        category: "",
        stock: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      name: "",
      description: "",
      price: "",
      category: "",
      stock: "",
    });
  };

  const handleSaveItem = async () => {
    if (!formData.name || !formData.price) {
      alert("Please fill in required fields (Name and Price)");
      return;
    }

    try {
      const itemData = {
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        category: formData.category || null,
        stock_quantity: parseInt(formData.stock) || 0,
      };

      if (editingItem) {
        // Update existing item
        await api.updateItem(editingItem.id, itemData);
        await loadItems();
        await loadStats();
        handleCloseDialog();
      } else {
        // Add new item
        await api.createItem(itemData);
        await loadItems();
        await loadStats();
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Error saving item:", error);
      alert("Failed to save item: " + error.message);
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await api.deleteItem(id);
        await loadItems();
        await loadStats();
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Failed to delete item: " + error.message);
      }
    }
  };

  // Format price with BHD
  const formatPrice = (price) => {
    if (!price) return "0.000 BHD";
    return `${parseFloat(price).toFixed(3)} BHD`;
  };

  // Prepare table data
  const tableColumns = [
    { name: "name", align: "left" },
    { name: "category", align: "left" },
    { name: "price", align: "center" },
    { name: "stock", align: "center" },
    { name: "status", align: "center" },
    { name: "actions", align: "center" },
  ];

  const tableRows = items.map((item) => ({
    name: (
      <VuiTypography 
        variant="button" 
        color="white" 
        fontWeight="medium"
        sx={{
          fontFamily: containsArabic(item.name) 
            ? '"Tajawal", "Cairo", "Arial", "Helvetica", sans-serif' 
            : '"Roboto", "Arial", sans-serif',
          direction: containsArabic(item.name) ? 'rtl' : 'ltr',
          textAlign: containsArabic(item.name) ? 'right' : 'left',
        }}
      >
        {item.name}
      </VuiTypography>
    ),
    category: (
      <VuiTypography 
        variant="button" 
        color={text.main} 
        fontWeight="regular"
        sx={{
          fontFamily: containsArabic(item.category || '') 
            ? '"Tajawal", "Cairo", "Arial", "Helvetica", sans-serif' 
            : '"Roboto", "Arial", sans-serif',
          direction: containsArabic(item.category || '') ? 'rtl' : 'ltr',
          textAlign: containsArabic(item.category || '') ? 'right' : 'left',
        }}
      >
        {item.category || "N/A"}
      </VuiTypography>
    ),
    price: (
      <VuiTypography variant="button" color="white" fontWeight="bold">
        {formatPrice(item.price)}
      </VuiTypography>
    ),
    stock: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {item.stock_quantity || 0}
      </VuiTypography>
    ),
    status: (
      <VuiTypography
        variant="button"
        color={item.status === "active" ? "success" : "error"}
        fontWeight="medium"
      >
        {item.status === "active" ? "Active" : "Out of Stock"}
      </VuiTypography>
    ),
    actions: (
      <VuiBox display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={() => handleOpenDialog(item)}
          sx={{
            color: info.main,
            "&:hover": {
              backgroundColor: "rgba(0, 117, 255, 0.1)",
            },
          }}
        >
          <MdEdit size={18} />
        </IconButton>
        <IconButton
          onClick={() => handleDeleteItem(item.id)}
          sx={{
            color: "error.main",
            "&:hover": {
              backgroundColor: "rgba(231, 26, 26, 0.1)",
            },
          }}
        >
          <MdDelete size={18} />
        </IconButton>
      </VuiBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox 
        sx={{ 
          pb: "0 !important", 
          "& > *:last-child": { marginBottom: 0 },
          minHeight: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
        py={3}
      >
        {/* Statistics Cards */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Items", fontWeight: "regular" }}
                count={stats.total}
                percentage={{ color: "info", text: "All items" }}
                icon={{ color: "info", component: <IoCubeOutline size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Active Items" }}
                count={stats.active}
                percentage={{ color: "success", text: "In stock" }}
                icon={{ color: "info", component: <IoCubeOutline size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Out of Stock" }}
                count={stats.outOfStock}
                percentage={{ color: "error", text: "Need restock" }}
                icon={{ color: "info", component: <IoCubeOutline size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Value" }}
                count={formatPrice(stats.totalValue)}
                percentage={{ color: "info", text: "Inventory value" }}
                icon={{ color: "info", component: <IoCubeOutline size="22px" color="white" /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>

        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Items Management
            </VuiTypography>
            <VuiTypography variant="button" color={text.main} sx={{ mt: 0.5 }}>
              Add and manage your items
            </VuiTypography>
          </VuiBox>
          <VuiButton
            variant="gradient"
            color="info"
            startIcon={<MdAdd size={18} />}
            onClick={() => handleOpenDialog()}
          >
            Add Item
          </VuiButton>
        </VuiBox>

        <Grid container spacing={3} sx={{ mb: "0 !important", flex: 1 }}>
          <Grid item xs={12}>
            <Card sx={{ mb: 0, height: "100%", display: "flex", flexDirection: "column" }}>
              <VuiBox
                p={3}
                sx={{
                  background: linearGradient(card.main, card.state, card.deg),
                  borderBottom: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <IoCubeOutline size={20} color={info.main} />
                    <VuiTypography variant="lg" color="white" fontWeight="bold">
                      My Items ({items.length})
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <IconButton
                      onClick={() => setViewMode("table")}
                      sx={{
                        color: viewMode === "table" ? info.main : text.main,
                        backgroundColor: viewMode === "table" ? "rgba(0, 117, 255, 0.1)" : "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(0, 117, 255, 0.15)",
                        },
                      }}
                    >
                      <BsTable size={18} />
                    </IconButton>
                    <IconButton
                      onClick={() => setViewMode("grid")}
                      sx={{
                        color: viewMode === "grid" ? info.main : text.main,
                        backgroundColor: viewMode === "grid" ? "rgba(0, 117, 255, 0.1)" : "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(0, 117, 255, 0.15)",
                        },
                      }}
                    >
                      <BsGrid3X3 size={18} />
                    </IconButton>
                  </VuiBox>
                </VuiBox>
              </VuiBox>

              <VuiBox
                p={items.length === 0 && !loading ? 0 : 2}
                sx={{
                  pb: 2,
                  flex: 1,
                  "& th": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                  },
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[700]}`,
                    },
                  },
                }}
              >
                {loading ? (
                  <VuiBox p={4} textAlign="center" sx={{ minHeight: "300px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress color="info" />
                    <VuiTypography variant="button" color={text.main} mt={2}>
                      Loading items...
                    </VuiTypography>
                  </VuiBox>
                ) : items.length > 0 ? (
                  viewMode === "table" ? (
                    <Table columns={tableColumns} rows={tableRows} />
                  ) : (
                    <Grid container spacing={3} sx={{ p: 2 }}>
                      {items.map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                          <Card
                            sx={{
                              background: linearGradient(card.main, card.state, card.deg),
                              backdropFilter: "blur(120px)",
                              border: `1px solid ${borderCol.navbar}`,
                              borderRadius: "16px",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 12px 32px rgba(0, 117, 255, 0.25)",
                                borderColor: info.main,
                              },
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              overflow: "hidden",
                            }}
                          >
                            <VuiBox p={2.5} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {/* Header Section */}
                              <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                <VuiBox sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                                  <VuiTypography 
                                    variant="h6" 
                                    color="white" 
                                    fontWeight="bold" 
                                    sx={{ 
                                      fontSize: "17px", 
                                      mb: 0.5,
                                      lineHeight: 1.3,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      fontFamily: containsArabic(item.name) 
                                        ? '"Tajawal", "Cairo", "Arial", "Helvetica", sans-serif' 
                                        : '"Roboto", "Arial", sans-serif',
                                      direction: containsArabic(item.name) ? 'rtl' : 'ltr',
                                      textAlign: containsArabic(item.name) ? 'right' : 'left',
                                    }}
                                  >
                                    {item.name}
                                  </VuiTypography>
                                </VuiBox>
                                <VuiBox
                                  component="span"
                                  sx={{
                                    backgroundColor: item.status === "active" ? "rgba(0, 227, 26, 0.15)" : "rgba(227, 26, 26, 0.15)",
                                    color: item.status === "active" ? "success.main" : "error.main",
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: "8px",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.status === "active" ? "Active" : "Out"}
                                </VuiBox>
                              </VuiBox>

                              {/* Divider */}
                              <VuiBox 
                                sx={{ 
                                  height: "1px", 
                                  background: `linear-gradient(90deg, transparent, ${borderCol.navbar}, transparent)`,
                                  my: 0.5,
                                }} 
                              />

                              {/* Category Section */}
                              <VuiBox>
                                <VuiTypography 
                                  variant="caption" 
                                  color={text.main} 
                                  sx={{ 
                                    fontSize: "10px", 
                                    opacity: 0.6,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    fontWeight: "medium",
                                    mb: 0.5,
                                    display: "block",
                                  }}
                                >
                                  Category
                                </VuiTypography>
                                <VuiTypography 
                                  variant="button" 
                                  color="white" 
                                  sx={{ 
                                    fontSize: "14px",
                                    fontWeight: "medium",
                                    fontFamily: containsArabic(item.category || '') 
                                      ? '"Tajawal", "Cairo", "Arial", "Helvetica", sans-serif' 
                                      : '"Roboto", "Arial", sans-serif',
                                    direction: containsArabic(item.category || '') ? 'rtl' : 'ltr',
                                    textAlign: containsArabic(item.category || '') ? 'right' : 'left',
                                  }}
                                >
                                  {item.category || "N/A"}
                                </VuiTypography>
                              </VuiBox>

                              {/* Price and Stock Section */}
                              <VuiBox 
                                display="flex" 
                                justifyContent="space-between" 
                                alignItems="flex-start" 
                                gap={2}
                                sx={{
                                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                                  borderRadius: "12px",
                                  p: 1.5,
                                  border: `1px solid ${borderCol.navbar}`,
                                }}
                              >
                                <VuiBox sx={{ flex: 1 }}>
                                  <VuiTypography 
                                    variant="caption" 
                                    color={text.main} 
                                    sx={{ 
                                      fontSize: "10px", 
                                      opacity: 0.6,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      fontWeight: "medium",
                                      mb: 0.5,
                                      display: "block",
                                    }}
                                  >
                                    Price
                                  </VuiTypography>
                                  <VuiTypography 
                                    variant="button" 
                                    color="white" 
                                    fontWeight="bold" 
                                    sx={{ 
                                      fontSize: "18px",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {formatPrice(item.price)}
                                  </VuiTypography>
                                </VuiBox>
                                <VuiBox 
                                  sx={{ 
                                    width: "1px", 
                                    backgroundColor: borderCol.navbar,
                                    alignSelf: "stretch",
                                  }} 
                                />
                                <VuiBox sx={{ flex: 1, textAlign: "right" }}>
                                  <VuiTypography 
                                    variant="caption" 
                                    color={text.main} 
                                    sx={{ 
                                      fontSize: "10px", 
                                      opacity: 0.6,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      fontWeight: "medium",
                                      mb: 0.5,
                                      display: "block",
                                    }}
                                  >
                                    Stock
                                  </VuiTypography>
                                  <VuiTypography 
                                    variant="button" 
                                    color="white" 
                                    fontWeight="bold" 
                                    sx={{ 
                                      fontSize: "18px",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {item.stock_quantity || 0}
                                  </VuiTypography>
                                </VuiBox>
                              </VuiBox>

                              {/* Actions Section */}
                              <VuiBox 
                                display="flex" 
                                alignItems="center" 
                                gap={1} 
                                mt="auto" 
                                pt={1.5} 
                                borderTop={`1px solid ${borderCol.navbar}`}
                              >
                                <IconButton
                                  onClick={() => handleOpenDialog(item)}
                                  sx={{
                                    color: info.main,
                                    flex: 1,
                                    backgroundColor: "rgba(0, 117, 255, 0.1)",
                                    borderRadius: "8px",
                                    py: 1,
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 117, 255, 0.2)",
                                      transform: "scale(1.05)",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <MdEdit size={18} />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDeleteItem(item.id)}
                                  sx={{
                                    color: "error.main",
                                    flex: 1,
                                    backgroundColor: "rgba(227, 26, 26, 0.1)",
                                    borderRadius: "8px",
                                    py: 1,
                                    "&:hover": {
                                      backgroundColor: "rgba(227, 26, 26, 0.2)",
                                      transform: "scale(1.05)",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <MdDelete size={18} />
                                </IconButton>
                              </VuiBox>
                            </VuiBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )
                ) : (
                  <VuiBox p={4} textAlign="center" sx={{ minHeight: "300px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <IoCubeOutline size={64} color={text.main} style={{ opacity: 0.3 }} />
                    <VuiTypography variant="h6" color={text.main} mt={2}>
                      No items yet
                    </VuiTypography>
                    <VuiTypography variant="button" color={text.main} sx={{ mt: 1 }}>
                      Click "Add Item" to create your first item
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: dark.body,
            backgroundImage: "none",
            borderRadius: "16px",
            border: `1px solid ${borderCol.navbar}`,
          },
        }}
      >
        <DialogTitle sx={{ color: "white", borderBottom: `1px solid ${borderCol.navbar}` }}>
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            {editingItem ? "Edit Item" : "Add New Item"}
          </VuiTypography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Item Name *
              </VuiTypography>
              <TextField
                fullWidth
                placeholder="Enter item name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    borderRadius: "8px",
                    border: `1px solid ${borderCol.navbar}`,
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: text.main,
                    opacity: 0.6,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Description
              </VuiTypography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter item description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    borderRadius: "8px",
                    border: `1px solid ${borderCol.navbar}`,
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: text.main,
                    opacity: 0.6,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Price (BHD) *
              </VuiTypography>
              <TextField
                fullWidth
                type="number"
                step="0.001"
                placeholder="e.g., 99.999"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    borderRadius: "8px",
                    border: `1px solid ${borderCol.navbar}`,
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: text.main,
                    opacity: 0.6,
                  },
                }}
              />
              <VuiTypography variant="caption" color={text.main} sx={{ mt: 0.5, display: "block" }}>
                Price will be displayed as BHD automatically
              </VuiTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Category
              </VuiTypography>
              <TextField
                fullWidth
                placeholder="e.g., Electronics, Software"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    borderRadius: "8px",
                    border: `1px solid ${borderCol.navbar}`,
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: text.main,
                    opacity: 0.6,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Stock Quantity
              </VuiTypography>
              <TextField
                fullWidth
                type="number"
                placeholder="Enter stock quantity"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    borderRadius: "8px",
                    border: `1px solid ${borderCol.navbar}`,
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: text.main,
                    opacity: 0.6,
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${borderCol.navbar}` }}>
          <VuiButton variant="outlined" color="text" onClick={handleCloseDialog}>
            Cancel
          </VuiButton>
          <VuiButton variant="gradient" color="info" onClick={handleSaveItem}>
            {editingItem ? "Update Item" : "Add Item"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Items;
